import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus, Sparkles, X } from 'lucide-react';
import { SelectDropdown } from '../SelectDropdown';
interface TopicEditorProps {
  heading: string;
  activeTerms: string[];
  /** Terms the system pulled out of the notes, used to show provenance. */
  detectedTerms: string[];
  suggestedTerms: string[];
  suggestedLabel: string;
  vocabulary: string[];
  onToggleTerm: (term: string) => void;
  emptyHint?: string;
  /** Hidden when a dedicated topic input is already visible alongside. */
  hideAddControl?: boolean;
  footer?: React.ReactNode;
}
export function TopicEditor({
  heading,
  activeTerms,
  detectedTerms,
  suggestedTerms,
  suggestedLabel,
  vocabulary,
  onToggleTerm,
  emptyHint = 'Nothing selected yet — pick a topic below.',
  hideAddControl = false,
  footer
}: TopicEditorProps) {
  const unusedVocabulary = vocabulary.filter(term => !activeTerms.includes(term) && !suggestedTerms.includes(term));
  const hasCustomTerms = activeTerms.some(term => !detectedTerms.includes(term));
  return <div data-id="element-1950" data-mp="1xkctgn9847qn">
      <div className="flex items-baseline justify-between gap-3" data-id="element-1951" data-mp="1xkmnclxe2z1m">
        <div className="text-xs font-semibold uppercase tracking-wider text-[#30718D]" data-id="element-1952" data-mp="1xmftm043bh93">
          {heading}
        </div>
        <span className="text-xs text-gray-500" data-id="element-1953" data-mp="27iiw572uthwk">
          {activeTerms.length} selected
        </span>
      </div>

      <div className="mt-2 flex min-h-[26px] flex-wrap items-center gap-1.5" data-id="element-1954" data-mp="2lha3man7o5bq">
        <AnimatePresence initial={false} data-id="element-1955" data-mp="2gj8jrnhyo1z7">
          {activeTerms.map(term => {
          const detected = detectedTerms.includes(term);
          return <motion.button key={term} type="button" onClick={() => onToggleTerm(term)} title={detected ? 'Detected in your notes — click to remove' : 'Added by you — click to remove'} initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.16,
            ease: [0.23, 1, 0.32, 1]
          }} className={`inline-flex items-center gap-1 rounded py-1 pl-2 pr-1.5 text-xs font-medium transition-colors ${detected ? 'bg-white text-[#30718D] hover:bg-gray-50' : 'bg-[#30718D] text-white hover:bg-[#265a70]'}`} data-id="element-1956" data-mp="2v9xz922gg38v">
                {detected && <Sparkles size={11} aria-hidden="true" data-id="element-1957" data-mp="2kjs6qiiy1j61" />}
                {term}
                <X size={12} aria-hidden="true" data-id="element-1958" data-mp="2pm103fzqan8o" />
              </motion.button>;
        })}
        </AnimatePresence>

        {activeTerms.length === 0 && <span className="text-xs text-gray-600" data-id="element-1959" data-mp="v76rdl9xaum8">{emptyHint}</span>}
      </div>

      {suggestedTerms.length > 0 && <div className="mt-4" data-id="element-1960" data-mp="z91u8u7i5g24">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1961" data-mp="zaczkyglivvc">
            {suggestedLabel}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5" data-id="element-1962" data-mp="mxiwhpdj4pcw">
            <AnimatePresence initial={false} data-id="element-1963" data-mp="mw7sppk33hls">
              {suggestedTerms.map(term => <motion.button key={term} type="button" onClick={() => onToggleTerm(term)} initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.16,
            ease: [0.23, 1, 0.32, 1]
          }} className="inline-flex items-center gap-1 rounded border border-dashed border-[#30718D]/40 bg-white py-1 pl-1.5 pr-2 text-xs font-medium text-[#30718D] transition-colors hover:border-[#30718D] hover:bg-[#30718D]/5" data-id="element-1964" data-mp="hwlhtmrqadg5">
                  <Plus size={12} aria-hidden="true" data-id="element-1965" data-mp="3hdjrqof3sctd" />
                  {term}
                </motion.button>)}
            </AnimatePresence>
          </div>
        </div>}

      {!hideAddControl && <div className="mt-4" data-id="element-1966" data-mp="1dx50avotzbkl">
        <SelectDropdown ariaLabel="Add a topic" size="sm" scrollMenu icon={<Plus size={14} data-id="element-1968" data-mp="102lxwk8w082a" />} value="" onChange={onToggleTerm} options={[{
        value: '',
        label: 'Add a topic'
      }, ...unusedVocabulary.map(term => ({
        value: term,
        label: term
      }))]} data-id="element-1967" data-mp="1dxem072uewkh" />
      </div>}

      {footer}

      {hasCustomTerms && <p className="mt-3 text-[11px] leading-relaxed text-gray-500" data-id="element-1969" data-mp="1iu504ov37i39">
          Chips with a spark came from your notes. Solid teal chips you added
          yourself.
        </p>}
    </div>;
}